import { useState, useCallback, memo } from 'react';
import { assessmentData } from '../data/assessmentData';

const CompanyInfoForm = memo(({ companyName, email, onCompanyNameChange, onEmailChange }) => (
  <div className="space-y-4">
    <div className="flex flex-col sm:flex-row sm:items-start sm:gap-8">
      <div className="sm:w-1/3 mb-2 sm:mb-0">
        <label htmlFor="companyName" className="block text-base text-gray-700">
          Your company name <span className="text-sm text-gray-500">(optional)</span>
        </label>
      </div>
      <div className="sm:w-2/3">
        <input
          type="text"
          id="companyName"
          name="company_name"
          autoComplete="organization"
          value={companyName}
          onChange={onCompanyNameChange}
          placeholder="We'll include it in your report"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
        />
      </div>
    </div>

    <div className="flex flex-col sm:flex-row sm:items-start sm:gap-8">
      <div className="sm:w-1/3 mb-2 sm:mb-0">
        <label htmlFor="email" className="block text-base text-gray-700">
          Contact email <span className="text-sm text-gray-500">(optional)</span>
        </label>
      </div>
      <div className="sm:w-2/3">
        <input
          type="email"
          id="email"
          name="business_email"
          autoComplete="email"
          spellCheck="false"
          value={email}
          onChange={onEmailChange}
          placeholder="If you'd like a consultation"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
        />
      </div>
    </div>
  </div>
));

function Introduction({ text, onStart, skipCompanyInfo, showOnlyCompanyInfo, onBack }) {
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  
  // Check if the app is running in an iframe
  const isEmbedded = window !== window.parent;

  // Keep ISO date for data handling but display formatted date
  const currentDate = new Date().toISOString().split('T')[0];
  const formattedDate = new Date().toLocaleDateString();

  const handleStart = useCallback(() => {
    if (showOnlyCompanyInfo) {
      onStart({ 
        companyName, 
        email, 
        assessmentDate: currentDate,
        displayDate: formattedDate
      });
    } else {
      onStart();
    }
  }, [showOnlyCompanyInfo, companyName, email, currentDate, formattedDate, onStart]);

  const handleTestRun = useCallback(() => {
    // Create answers object with all questions set to "Strongly Agree" (value: 5)
    const testAnswers = {};
    assessmentData.sections.forEach(section => {
      section.questions.forEach((_, index) => {
        testAnswers[`${section.id}-${index}`] = 5;
      });
    });

    // Call onStart with test data
    onStart({ 
      companyName: 'Test Company',
      email: 'test@example.com',
      assessmentDate: currentDate,
      displayDate: formattedDate,
      testAnswers
    });
  }, [currentDate, formattedDate, onStart]);

  const handleCompanyNameChange = useCallback((e) => {
    setCompanyName(e.target.value);
  }, []);

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
  }, []);

  return (
    <div className="bg-white shadow rounded-lg p-6">
      {!showOnlyCompanyInfo && (
        <>
          <div className="mb-10">
            {!isEmbedded && (
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                Predictable Performance Assessment
              </h1>
            )}
            <p className="text-gray-700 mb-6">
              Do you have a clear understanding of how your business is performing today? Can you confidently predict its future success? Our online assessment tool provides a detailed business health check, helping you:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="flex-shrink-0 text-green-500 mr-2">✔</span>
                <span className="text-gray-700">Evaluate your current business performance</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 text-green-500 mr-2">✔</span>
                <span className="text-gray-700">Forecast future trends with data-driven insights</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 text-green-500 mr-2">✔</span>
                <span className="text-gray-700">Keep teams aligned and accountable with analytics</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 text-green-500 mr-2">✔</span>
                <span className="text-gray-700">Identify and implement necessary course corrections</span>
              </li>
            </ul>
          </div>

          <p className="text-gray-500 mb-6 text-x1">
            {text || assessmentData.introduction}
          </p>
        </>
      )}

      {showOnlyCompanyInfo && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            One last thing
          </h2>
          <div className="bg-primary-50 border-l-4 border-primary p-4 rounded mb-4">
            <p className="text-primary-900">
              Provide a few more details to personalize your report
            </p>
          </div>
          <div className="space-y-8">
            <CompanyInfoForm
              companyName={companyName}
              email={email}
              onCompanyNameChange={handleCompanyNameChange}
              onEmailChange={handleEmailChange}
            />
          </div>
        </div>
      )}

      <div className={`flex ${showOnlyCompanyInfo ? 'flex-col sm:flex-row' : 'justify-center'} gap-4`}>
        {showOnlyCompanyInfo && onBack && (
          <button
            onClick={onBack}
            className="px-6 py-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
          >
            Back
          </button>
        )}
        
        <button
          onClick={handleStart}
          className="px-6 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90"
        >
          {showOnlyCompanyInfo ? 'Complete Assessment' : 'Start Assessment'}
        </button>

        {process.env.NODE_ENV === 'development' && !showOnlyCompanyInfo && (
          <button
            onClick={handleTestRun}
            className="px-6 py-2 rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
          >
            Test Run (All 5)
          </button>
        )}
      </div>
    </div>
  );
}

export default Introduction; 